// Service API pour frontend
// FIX-001 (refs AUD-001): envoi du token d'auth si présent (localStorage calibre_rag_api_key)
import { API_CONFIG, apiJSON } from '../config/api';
import { getAuthHeaders as getAuthHeadersUtil, AUTH_STORAGE_KEY, notifyUnauthorized, withSseToken } from '../utils/apiAuth';

function getAuthHeaders() {
  return getAuthHeadersUtil();
}

class ApiService {
    constructor() {
        this.baseUrl = API_CONFIG.API_BASE_URL.replace(/\/$/, '');
    }

    /**
     * Délègue à la couche HTTP unique (config/api).
     *
     * L'implémentation précédente plaçait `timeout` dans les options de `fetch`,
     * où la clé est ignorée : aucun des ~40 appels de ce service n'était
     * réellement borné dans le temps. Passer par `apiJSON` apporte le timeout
     * (AbortController), le retry limité aux méthodes idempotentes, la
     * normalisation d'URL et la gestion du 401 — sans changer le contrat rendu
     * aux appelants : JSON en cas de succès, exception portant `.status` sinon.
     */
    async request(endpoint, options = {}) {
        return apiJSON(endpoint, options);
    }
    
    async getBooks() {
        return this.request('/books');
    }
    
    async search(query, filters = {}) {
        const params = new URLSearchParams({ q: query, ...filters });
        return this.request(`/search?${params}`);
    }
    
    async askRAG(question) {
        return this.request('/ask', {
            method: 'POST',
            body: JSON.stringify({ question })
        });
    }
    
    async uploadFile(file) {
        const formData = new FormData();
        formData.append('file', file);
        
        const opts = { method: 'POST', body: formData, headers: getAuthHeaders() };
        // FormData: ne pas envoyer Content-Type (le navigateur ajoute le boundary)
        return this.request('/upload', opts);
    }

    // FIX-005 (refs AUD-004): méthodes alignées sur les routes backend
    async getAnalyticsDashboard(days = 7) {
        return this.request(`/analytics/dashboard?days=${days}`);
    }
    async getHealth() {
        return this.request('/health');
    }
    async getVectorStoreStatus() {
        return this.request('/vector-store/status');
    }
    async getSystemOverview() {
        return this.request('/system/overview');
    }
    async getProjects() {
        return this.request('/projects');
    }
    async createProject(data) {
        return this.request('/projects', { method: 'POST', body: JSON.stringify(data) });
    }
    async getProject(id) {
        return this.request(`/projects/${id}`);
    }
    async updateProject(id, data) {
        return this.request(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(data) });
    }
    async deleteProject(id) {
        return this.request(`/projects/${id}`, { method: 'DELETE' });
    }
    async addBookToProject(projectId, bookId) {
        // La route backend est POST /api/projects/{id}/books/{book_id} : le
        // chemin `add-book` renvoyait 404, donc « Ajouter des livres » à un
        // projet n'a jamais fonctionné.
        return this.request(`/projects/${projectId}/books/${bookId}`, { method: 'POST' });
    }
    async getCollections() {
        return this.request('/collections/');
    }
    async getCollection(id) {
        return this.request(`/collections/${id}`);
    }
    async getAutomatedIngestionStatus() {
        return this.request('/automated-ingestion/status');
    }
    async startAutomatedIngestion() {
        return this.request('/automated-ingestion/start', { method: 'POST' });
    }
    async getAutomatedIngestionStats() {
        return this.request('/automated-ingestion/stats');
    }
    async getCoverExtractionStatus() {
        return this.request('/cover-extraction/status');
    }
    async extractCovers() {
        return this.request('/cover-extraction/extract', { method: 'POST' });
    }
    async getCoverExtractionBooks() {
        return this.request('/cover-extraction/books');
    }
    async getNotifications(limit = 10) {
        return this.request(`/notifications?limit=${limit}`);
    }
    async searchAdvanced(params) {
        const qs = new URLSearchParams(params).toString();
        return this.request(`/search/advanced?${qs}`);
    }
    async listTasks() {
        // La route backend est /api/tasks/active (il n'existe pas de GET /api/tasks).
        return this.request('/tasks/active');
    }
    async clearCache(type) {
        const q = type ? `?cache_type=${encodeURIComponent(type)}` : '';
        return this.request(`/cache/clear${q}`, { method: 'POST' });
    }
    async createConversation() {
        return this.request('/conversations', { method: 'POST', body: JSON.stringify({}) });
    }
    async getConversation(id) {
        return this.request(`/conversations/${id}`);
    }
    async askConversation(conversationId, question) {
        return this.request(`/conversations/${conversationId}/ask`, { method: 'POST', body: JSON.stringify({ question }) });
    }

    // --- Admin & système ---
    async getAdminHealth() {
        return this.request('/admin/health');
    }
    async getAdminLogs({ service = 'backend', limit = 50, level } = {}) {
        const params = new URLSearchParams({ service, limit: String(limit) });
        if (level) params.set('level', level);
        return this.request(`/admin/logs?${params.toString()}`);
    }
    async getBackupStatus() {
        return this.request('/backup/status');
    }
    async listBackups() {
        return this.request('/backup/list');
    }
    async createBackup() {
        return this.request('/backup/create', { method: 'POST' });
    }
    async restoreBackup(name) {
        return this.request(`/backup/restore/${encodeURIComponent(name)}`, { method: 'POST' });
    }
    async deleteBackup(name) {
        return this.request(`/backup/delete/${encodeURIComponent(name)}`, { method: 'DELETE' });
    }

    // --- Curation de contenus externes ---
    async proposeCuration(url, sourceContext) {
        const body = { url };
        if (sourceContext) body.source_context = sourceContext;
        return this.request('/curation/propose', {
            method: 'POST',
            body: JSON.stringify(body),
        });
    }
    async listCuration(status, limit = 50) {
        const params = new URLSearchParams();
        if (status) params.set('status', status);
        if (limit) params.set('limit', String(limit));
        const qs = params.toString();
        return this.request(`/curation/${qs ? `?${qs}` : ''}`);
    }
    async getCurationStats() {
        return this.request('/curation/stats');
    }
    async getCurationItem(id) {
        return this.request(`/curation/${id}`);
    }
    async acceptCuration(id) {
        return this.request(`/curation/${id}/accept`, { method: 'POST' });
    }
    async rejectCuration(id) {
        return this.request(`/curation/${id}/reject`, { method: 'POST' });
    }
    async deleteCuration(id) {
        return this.request(`/curation/${id}`, { method: 'DELETE' });
    }

    /**
     * SSE (FIX-005/006): s'abonner à un flux Server-Sent Events.
     * @param {string} path - chemin relatif à baseUrl (ex: /automated-ingestion/events)
     * @param {(data: any) => void} onMessage - callback pour chaque événement
     * @returns {() => void} fonction pour fermer l'EventSource
     */
    subscribeSSE(path, onMessage) {
        const url = withSseToken(`${this.baseUrl.replace(/\/api$/, '')}${path}`);
        const es = new EventSource(url);
        es.onmessage = (e) => { try { onMessage(JSON.parse(e.data)); } catch (_) { onMessage(e.data); } };
        es.onerror = () => es.close();
        return () => es.close();
    }

    /**
     * Persister question + réponse après un flux /rag/ask/stream (POST /api/conversations/{id}/commit-stream).
     */
    async commitStreamExchange(conversationId, payload) {
        const url = `${this.baseUrl.replace(/\/$/, '')}/conversations/${encodeURIComponent(conversationId)}/commit-stream`;
        const authHeaders = getAuthHeaders();
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...authHeaders,
            },
            body: JSON.stringify(payload),
        });
        if (response.status === 401) {
            notifyUnauthorized();
            const err = new Error('Authentification requise');
            err.status = 401;
            throw err;
        }
        if (!response.ok) {
            let message = response.statusText;
            try {
                const body = await response.json();
                if (body.detail) message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
            } catch (_) {}
            throw new Error(message);
        }
        return response.json();
    }

    /**
     * RAG agentique : POST /api/ask/stream (SSE). EventSource ne supporte pas POST — fetch + ReadableStream.
     * @param {{ question: string, method?: string, k?: number, bookFilters?: string, projectId?: number|string, model?: string }} params
     * @param {{ onChunk?: (t: string) => void, onHighlight?: (ev: object) => void, onGenUI?: (payload: object) => void, onMeta?: (ev: object) => void, onStateChange?: (ev: object) => void, onDone?: (ev: object) => void, onError?: (err: Error|object) => void, signal?: AbortSignal }} callbacks
     * @returns {() => void} cancel
     */
    streamAsk(params, callbacks = {}) {
        const {
            question,
            method = 'hybrid',
            k = 5,
            bookFilters,
            projectId,
            model,
        } = params;
        const {
            onChunk,
            onHighlight,
            onMeta,
            onGenUI,
            onStateChange,
            onDone,
            onError,
            signal: externalSignal,
        } = callbacks;

        const abortController = new AbortController();
        if (externalSignal) {
            if (externalSignal.aborted) abortController.abort();
            else externalSignal.addEventListener('abort', () => abortController.abort(), { once: true });
        }
        const signal = abortController.signal;

        const formData = new FormData();
        formData.append('question', question);
        formData.append('method', method);
        formData.append('k', String(k));
        if (bookFilters != null && String(bookFilters).trim()) {
            formData.append('book_filters', String(bookFilters).trim());
        }
        if (projectId != null && projectId !== '') {
            formData.append('project_id', String(projectId));
        }
        if (model) {
            formData.append('model', model);
        }

        const url = `${this.baseUrl.replace(/\/$/, '')}/ask/stream`;
        const authHeaders = getAuthHeaders();

        (async () => {
            try {
                const response = await fetch(url, {
                    method: 'POST',
                    body: formData,
                    headers: authHeaders,
                    signal,
                });

                if (response.status === 401) {
                    notifyUnauthorized();
                    const err = new Error('Authentification requise');
                    err.status = 401;
                    onError?.(err);
                    return;
                }
                if (!response.ok) {
                    let message = response.statusText;
                    try {
                        const body = await response.json();
                        if (body.detail) message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
                    } catch (_) {}
                    onError?.(new Error(message));
                    return;
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    onError?.(new Error('Corps de réponse illisible'));
                    return;
                }

                const decoder = new TextDecoder();
                const parser = new SseFrameParser();

                const dispatchEvent = (ev) => {
                    if (!ev || typeof ev !== 'object') return;
                    const t = ev.type;
                    if (t === 'token' && ev.text != null) {
                        onChunk?.(ev.text);
                    } else if (t === 'genui' && ev.payload != null) {
                        onGenUI?.(ev.payload);
                    } else if (t === 'quote_span') {
                        onHighlight?.(ev);
                    } else if (t === 'meta') {
                        onMeta?.(ev);
                        onStateChange?.(ev);
                    } else if (t === 'step') {
                        onStateChange?.(ev);
                    } else if (t === 'done') {
                        onDone?.(ev);
                    } else if (t === 'error') {
                        onError?.(ev.message ? new Error(ev.message) : ev);
                    }
                };

                const dispatchPayloads = (payloads) => {
                    for (const payload of payloads) {
                        try {
                            dispatchEvent(JSON.parse(payload));
                        } catch (e) {
                            console.warn('SSE parse:', e, payload.slice(0, 80));
                        }
                    }
                };

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    dispatchPayloads(parser.push(decoder.decode(value, { stream: true })));
                }
                // Le serveur peut fermer le flux juste apres la derniere trame,
                // sans ligne vide finale : ne pas perdre `done` pour autant.
                dispatchPayloads(parser.push(decoder.decode()));
                dispatchPayloads(parser.flush());
            } catch (e) {
                if (e.name === 'AbortError') return;
                onError?.(e);
            }
        })();

        return () => abortController.abort();
    }
}

/**
 * Découpe un flux Server-Sent Events en charges utiles `data:`.
 *
 * Pourquoi cette classe existe. Le serveur répond via sse-starlette, dont le
 * séparateur de ligne par défaut est `\r\n` : chaque trame se termine par
 * `\r\n\r\n`. Le lecteur historique cherchait `\n\n`, qui n'apparaît jamais
 * dans cette séquence — aucune trame n'était extraite et la réponse RAG restait
 * invisible (constaté en production le 2026-09-22, #101). `EventSource` natif
 * tolère les deux fins de ligne ; ce lecteur `fetch` doit faire de même.
 *
 * Contrat : `push(texte)` rend les charges utiles des trames complètes reçues
 * jusque-là (une trame peut être coupée entre deux `push`) ; `flush()` rend ce
 * qui reste quand le flux se ferme sans ligne vide finale. Les commentaires
 * (`: ping`) et les autres champs (`event:`, `id:`) sont ignorés. Plusieurs
 * lignes `data:` d'une même trame sont jointes par `\n`, comme le veut la norme.
 */
export class SseFrameParser {
    constructor() {
        this.buffer = '';
    }

    push(text) {
        // Normaliser d'abord : `\r\n` et `\r` seuls deviennent `\n`. Un `\r`
        // en fin de morceau est retenu, au cas où le `\n` arrive au suivant.
        this.buffer += text;
        const events = [];
        const trailingCr = this.buffer.endsWith('\r');
        let normalized = (trailingCr ? this.buffer.slice(0, -1) : this.buffer).replace(/\r\n?/g, '\n');
        let sep;
        while ((sep = normalized.indexOf('\n\n')) !== -1) {
            const block = normalized.slice(0, sep);
            normalized = normalized.slice(sep + 2);
            const payload = SseFrameParser.payloadOf(block);
            if (payload != null) events.push(payload);
        }
        this.buffer = trailingCr ? `${normalized}\r` : normalized;
        return events;
    }

    flush() {
        const rest = this.buffer.replace(/\r\n?/g, '\n');
        this.buffer = '';
        const payload = SseFrameParser.payloadOf(rest);
        return payload != null ? [payload] : [];
    }

    static payloadOf(block) {
        const data = [];
        for (const line of block.split('\n')) {
            if (line.startsWith('data:')) data.push(line.slice(5).replace(/^ /, ''));
        }
        if (data.length === 0) return null;
        const payload = data.join('\n').trim();
        return payload ? payload : null;
    }
}

export default new ApiService();
export { getAuthHeaders, AUTH_STORAGE_KEY };
